import { observer } from 'mobx-react-lite';
import React from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  EditableText,
  ITableProps,
  Table,
  useToast,
} from '../../npaper';
import { Spinner } from '../../npaper/Spinner';
import { useStores } from '../../stores';
import {
  IRelation,
  IRelationSnapshotOut,
} from '../../stores/contacts/RelationStore';
import {
  IPaginationFilter,
  IQueryCache,
} from '../../stores/utils/modelWithCRUD';
import { ContactThumbnail } from './ContactThumbnail';

export interface IContactsTableProps extends ITableProps {
  filter?: IPaginationFilter<IRelationSnapshotOut>;
  onRemove?: (item: IRelation) => any;
}

export const ContactRelationsTable: React.FC<IContactsTableProps> = observer(
  ({ filter, onRemove, ...props }) => {
    const toaster = useToast();
    const { relationStore } = useStores();
    const [needsMore, setNeedsMore] = useState(true);

    const [res, setRes] = useState<IQueryCache<IRelation>>();

    useEffect(() => {
      let isSub = true;
      relationStore.fetchMore(filter).then((res) => {
        if (isSub) {
          setRes(res);
          setNeedsMore(false);
        }
      });
      return () => {
        isSub = false;
      };
    }, [relationStore, filter, needsMore]);

    return !res || !res.items || res.isLoading ? (
      <Spinner size={16} />
    ) : res.items.length > 0 ? (
      <>
        <Table {...props} onScrollNearBottom={() => setNeedsMore(true)}>
          <thead>
            <tr>
              <th className="sticky">
                <FormattedMessage
                  defaultMessage="Name"
                  description="Relation name"
                />
              </th>
              <th>
                <FormattedMessage
                  defaultMessage="Title"
                  description="Relation title"
                />
              </th>
              <th>
                <FormattedMessage
                  defaultMessage="Contact details"
                  description="Relation contact details"
                />
              </th>
              {onRemove && <th style={{ width: '32px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {res.items.map((relation) => (
              <tr key={relation.id}>
                {relation.contact ? (
                  <>
                    <td className="sticky">
                      <Box display="flex" height="100%" width="100%">
                        <ContactThumbnail contact={relation.contact} />
                      </Box>
                    </td>
                    <td className="p small">
                      <EditableText
                        value={relation.title || undefined}
                        placeholder="Titel"
                        autoHeight
                        onConfirm={(title) =>
                          relationStore
                            .updateItem(relation, { title: title })
                            .catch(toaster.showDefaultError)
                        }
                      />
                    </td>
                    <td>
                      <p className="small">
                        {relation.contact.emails.slice(0, 1).join('')}
                      </p>
                      <p className="small font-mono">
                        {[relation.contact.phone, relation.contact.mobile]
                          .filter((a) => !!a)
                          .join(', ')}
                      </p>
                    </td>
                    {onRemove && (
                      <td>
                        <Button
                          iconLeft="trash"
                          minimal={true}
                          marginLeft="auto"
                          onClick={async () => {
                            onRemove(relation);
                          }}
                        />
                      </td>
                    )}
                  </>
                ) : (
                  <td colSpan={100}>
                    <Box className="skeleton" height="32px"></Box>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    ) : (
      <Box is="p">
        <FormattedMessage defaultMessage="No contacts associated to company" />
      </Box>
    );
  }
);
